import {
  ParseResponse,
  HealthScoreResult,
  AdviceReport,
  FullReport,
  RuleListResponse,
  UserProjectSettings,
  DcmaAssessmentResult,
  LicenseStatus,
  RuleMetadataResponse,
  SCurves,
} from '../types';
import {
  getSession,
  getValidAccessToken,
  refreshOnce,
  type Session,
} from '../auth/session';

/**
 * API client. By default talks to a same-origin sc-node-js (Vite dev server proxies
 * /api → :3000). When embedded, you can override the base URL via window.__SC_API_BASE__.
 */
function baseUrl(): string {
  return (window as any).__SC_API_BASE__ ?? '';
}

function userId(): string {
  return (window as any).__SC_USER_ID__ ?? localStorage.getItem('sc-user-id') ?? 'embed-user';
}

/**
 * Exchanges a refresh token for a new session. Passed INTO the session module
 * rather than called by it, so that session.ts stays free of this file — it
 * imports session.ts, and the reverse would be a cycle.
 */
async function refreshSessionRequest(refreshToken: string): Promise<Session> {
  const response = await fetch(`${baseUrl()}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) throw new Error(`refresh failed: ${response.status}`);
  return response.json() as Promise<Session>;
}

/**
 * Attaches credentials to `headers`, returning whether a real bearer token was
 * used.
 *
 * TWO MODES, and the fallback is not dead code. When the API runs with
 * NODE_ENV!=production and no OIDC audience it still accepts X-User-Id, which
 * is what every local checkout and the whole Playwright suite rely on — they
 * have no Supabase project to sign in against. Once a session exists the
 * bearer token takes precedence, and a deployment that enforces auth ignores
 * the header entirely.
 */
async function applyAuth(headers: Headers): Promise<boolean> {
  const token = await getValidAccessToken(refreshSessionRequest);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
    return true;
  }
  headers.set('X-User-Id', userId());
  return false;
}

/**
 * A 5xx the CDN produced itself, before our origin was ever contacted.
 *
 * Vercel sets `x-vercel-error` only on failures it generated at the edge, and
 * that header is what makes this retry safe rather than reckless. A plain 502
 * is ambiguous — the request may have reached the API, done its work, and lost
 * only the response, so re-sending a POST could run a two-minute analysis
 * twice. This one is not ambiguous: the edge never opened a connection to us,
 * so the request provably had no effect and any method may be repeated.
 *
 * Concretely, this is `DNS_HOSTNAME_EMPTY` on the `/api/*` rewrite: roughly one
 * request in thirty through the edge, none direct to the API, and none of them
 * in nginx's access log (docs/deployed-walkthrough-2026-08-25.md #4b). DNS
 * resolves correctly and identically at both authoritative nameservers and
 * three public resolvers, so the fault is not ours to fix — but it is ours to
 * survive, and one retry turns a broken card into a hiccup nobody sees.
 *
 * Same-origin, so the header is readable: the SPA and the rewrite share an
 * origin by design, which is also why the browser never needs CORS here.
 */
function edgeFailedBeforeOrigin(response: Response): boolean {
  return response.status >= 500 && response.headers.has('x-vercel-error');
}

const EDGE_RETRY_DELAY_MS = 250;

async function fetchThroughEdge(url: string, init: RequestInit): Promise<Response> {
  const response = await fetch(url, init);
  if (!edgeFailedBeforeOrigin(response)) return response;
  // Once, not a loop. A second edge failure on the same call is no longer a
  // blip, and a caller that waits through repeated retries learns nothing it
  // could not learn faster from the error.
  await new Promise((resolve) => setTimeout(resolve, EDGE_RETRY_DELAY_MS));
  return fetch(url, init);
}

/**
 * The single fetch every API call goes through.
 *
 * The retry exists for the gap the expiry margin cannot cover: a token that
 * was valid when the request left and had expired by the time it arrived, or
 * one invalidated server-side mid-session. Bounded to ONE retry — a second
 * 401 after a fresh token means the problem is authorization, not staleness,
 * and retrying further would just loop.
 */
async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const usedBearer = await applyAuth(headers);
  const response = await fetchThroughEdge(`${baseUrl()}${path}`, { ...init, headers });

  if (response.status !== 401 || !usedBearer) return response;

  const session = getSession();
  if (!session) return response;
  const refreshed = await refreshOnce(session.refreshToken, refreshSessionRequest);
  if (!refreshed) return response;

  const retryHeaders = new Headers(init.headers);
  retryHeaders.set('Authorization', `Bearer ${refreshed.accessToken}`);
  return fetchThroughEdge(`${baseUrl()}${path}`, { ...init, headers: retryHeaders });
}

/**
 * A failed API call, carrying the status the message already spells out.
 *
 * The message is unchanged — every screen that renders `error.message` shows
 * exactly what it showed before — but the status is now readable without
 * parsing prose, which is what lets the query client tell "try again" apart
 * from "this will fail every time" (see main.tsx).
 */
export class HttpError extends Error {
  constructor(readonly status: number, body: string) {
    super(`${status}: ${body}`);
    this.name = 'HttpError';
  }
}

/**
 * Whether repeating a failed request could plausibly succeed.
 *
 * A 4xx is a verdict about the request itself — a missing project, an expired
 * plan, a body the server rejected — and asking twice only makes the empty
 * state arrive a second later. A 5xx, or a network error with no status at
 * all, is about the moment.
 */
export function isRetriableError(error: unknown): boolean {
  return !(error instanceof HttpError) || error.status >= 500;
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  const response = await authedFetch(path, { ...init, headers });
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new HttpError(response.status, text);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/**
 * The 202 an async compute route answers with, instead of the result.
 *
 * Told apart from a real result by `schemaVersion`, not by the HTTP status:
 * fetchJson has already discarded the status by the time we see the body, and
 * every envelope names itself. A queued run says 'job-1'; the results it
 * stands in for say 'delay-chain-1' and 'qsra-1'.
 */
interface QueuedJob {
  schemaVersion: string;
  id: string;
  type: string;
}

interface JobStatus<T> {
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed' | 'paused' | string;
  returnvalue: T | null;
  failedReason?: string;
}

const isQueued = (v: unknown): v is QueuedJob =>
  typeof v === 'object' && v != null
  && (v as QueuedJob).schemaVersion === 'job-1'
  && typeof (v as QueuedJob).id === 'string';

const POLL_MS = 2000;
/**
 * Give up after this long. Not a guess at how slow a run can be — it is how
 * long a browser tab should keep asking about one before something is clearly
 * wrong. The job itself is unaffected: it finishes, persists, and the next
 * page load reads the result out of Postgres.
 */
const POLL_TIMEOUT_MS = 30 * 60_000;
/**
 * How many polls in a row may fail before the run is given up on.
 *
 * Bounded rather than infinite so a genuinely unreachable API still surfaces
 * as an error rather than a spinner that never resolves; consecutive rather
 * than cumulative because what distinguishes a transient edge hiccup from a
 * real outage is precisely whether the next one also fails.
 */
const MAX_POLL_FAILURES = 5;

/**
 * POST a long compute request, and wait for its answer however it arrives.
 *
 * The route runs the work on the queue when it can and INLINE when it cannot
 * (Redis down, worker stopped, a dev checkout with neither), so both shapes
 * are normal and neither is an error. Discriminating on the envelope rather
 * than on the status code means this one function covers both without the
 * caller ever learning which happened — which is the property that lets the
 * async path be the default rather than an opt-in nobody dares switch on.
 *
 * Polling, not SSE, deliberately: the jobs route's own comment says the event
 * stream has no replay and that GET /jobs/:id is the source of truth. A
 * poll that survives a dropped connection is worth more here than one that
 * updates a second sooner.
 */
async function postAndAwait<T>(path: string, body: unknown): Promise<T> {
  const started = await fetchJson<T | QueuedJob>(`${path}?async=1`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!isQueued(started)) return started as T;

  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let consecutiveFailures = 0;
  for (;;) {
    await new Promise((r) => setTimeout(r, POLL_MS));

    let status: JobStatus<T>;
    try {
      status = await fetchJson<JobStatus<T>>(`/api/v1/jobs/${started.id}`);
      consecutiveFailures = 0;
    } catch (err) {
      // A FAILED POLL IS NOT A FAILED RUN, and conflating the two is the
      // easiest way to make queued work look less reliable than the blocking
      // call it replaced. The work is happening in another process; this
      // request is only asking after it. Measured on the deployed stack:
      // roughly one request in thirty through the Vercel rewrite returns 502
      // with X-Vercel-Error: DNS_HOSTNAME_EMPTY while the API itself answers
      // 20 out of 20 directly — so over the ~15 polls of a long run, giving up
      // on the first error would abandon most runs that were going to succeed.
      if (++consecutiveFailures >= MAX_POLL_FAILURES) throw err;
      continue;
    }

    if (status.state === 'completed') {
      // A completed job with no return value is a contract violation, not an
      // empty result — surfacing it as one would render an empty screen that
      // looks like a schedule with nothing in it.
      if (status.returnvalue == null) throw new Error('The analysis finished but returned no result.');
      return status.returnvalue;
    }
    if (status.state === 'failed') throw new Error(status.failedReason ?? 'The analysis failed.');
    if (Date.now() > deadline) throw new Error('The analysis is still running. Reload the page to see the result once it finishes.');
  }
}

/**
 * Multipart upload — deliberately bypasses fetchJson's JSON Content-Type
 * default (a FormData body needs the browser-computed multipart boundary,
 * not 'application/json').
 */
async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const response = await authedFetch(path, { method: 'POST', body: form });
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`${response.status}: ${text}`);
  }
  return response.json() as Promise<T>;
}

/**
 * F2b: the /reports/claim/{docx,pdf} downloads. Bypasses fetchJson (the response
 * is a binary blob, not JSON); same request body and gating as claimReport.
 */
async function claimReportBinary(
  format: 'docx' | 'pdf',
  filePaths: string[] | null,
  userProject?: UserProjectSettings,
  userProjectId?: string,
): Promise<Blob> {
  const response = await authedFetch(`/api/v1/reports/claim/${format}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filePaths: filePaths ?? undefined, userProject, userProjectId }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`${response.status}: ${text}`);
  }
  return response.blob();
}

export const api = {
  health: () => fetchJson<{ status: string }>('/health'),
  /**
   * `capabilities` is per-DEPLOYMENT, not per-build: .mpp conversion needs the
   * sidecar running, so the same commit answers differently on two servers.
   * Optional in the type because an older server simply omits it, and every
   * reader must fall back to the conservative answer rather than assuming.
   */
  version: () => fetchJson<{
    service: string;
    version: string;
    capabilities?: { mppConversion?: boolean };
  }>('/version'),
  rules: () => fetchJson<RuleListResponse>('/api/v1/analysis/rules'),

  /** UX1 — module license gating (Contract module rides the 'delay' app gate, same as the backend). */
  license: () => fetchJson<LicenseStatus>('/api/v1/license'),

  /** UX1 — GAO tab (Compliance): rule/category catalog to join against ruleOutputs.byRule. */
  ruleMetadata: () => fetchJson<RuleMetadataResponse>('/api/v1/analysis/rules/metadata'),

  parse: (filePath: string, runAnalysis = false, userProject?: UserProjectSettings) =>
    fetchJson<ParseResponse>('/api/v1/imports/parse', {
      method: 'POST',
      body: JSON.stringify({ filePath, runAnalysis, userProject }),
    }),

  /** Enhancement-queue item #1: upload a file, get back a filePath every other endpoint already accepts. */
  uploadFile: (file: File) =>
    uploadFile<{ filePath: string; fileName: string; uploadId: string; size: number }>('/api/v1/imports/upload', file),

  fullReport: (filePath: string, baselinePath?: string, userProject?: UserProjectSettings) =>
    fetchJson<FullReport>('/api/v1/reports/full', {
      method: 'POST',
      body: JSON.stringify({ filePath, baselinePath, userProject }),
    }),

  healthReport: (filePath: string, baselinePath?: string, userProject?: UserProjectSettings) =>
    fetchJson<HealthScoreResult>('/api/v1/reports/health', {
      method: 'POST',
      body: JSON.stringify({ filePath, baselinePath, userProject }),
    }),

  /**
   * DCMA-14 assessment of ONE schedule. Pass a filePath, or `null` plus a
   * userProjectId and the server grades the project's latest version from the
   * path it recorded at import — the project-first form UXR-3's gate sheets use,
   * since a browser has no server paths to send.
   */
  dcmaReport: (
    filePath: string | null,
    baselinePath?: string,
    userProject?: UserProjectSettings,
    userProjectId?: string,
  ) =>
    fetchJson<DcmaAssessmentResult>('/api/v1/reports/dcma', {
      method: 'POST',
      body: JSON.stringify({ filePath: filePath ?? undefined, baselinePath, userProject, userProjectId }),
    }),

  adviceReport: (filePath: string, baselinePath?: string, userProject?: UserProjectSettings) =>
    fetchJson<AdviceReport>('/api/v1/reports/advice', {
      method: 'POST',
      body: JSON.stringify({ filePath, baselinePath, userProject }),
    }),

  /**
   * The activity grid. Address it EITHER by filePath (legacy file-first viewer)
   * OR by userProjectId — with loadId to pick a version off the spine, or
   * without to mean the latest. The server refuses a request that names
   * neither, and a loadId from another project is a 404 rather than a quiet
   * fall back to the latest.
   */
  tasks: (params: {
    filePath?: string;
    userProjectId?: string;
    loadId?: string;
    filter?: 'all' | 'critical' | 'near' | 'completed' | 'in-progress' | 'not-started';
    search?: string;
    limit?: number;
    offset?: number;
    criticalDays?: number;
    nearCriticalDays?: number;
    /** Applied across the whole filtered set before paging — see the route. */
    sort?: TaskSortKey;
    dir?: 'asc' | 'desc';
  }) => {
    const qs = new URLSearchParams();
    if (params.filePath) qs.set('filePath', params.filePath);
    if (params.userProjectId) qs.set('userProjectId', params.userProjectId);
    if (params.loadId) qs.set('loadId', params.loadId);
    if (params.filter) qs.set('filter', params.filter);
    if (params.search) qs.set('search', params.search);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    if (params.criticalDays !== undefined) qs.set('criticalDays', String(params.criticalDays));
    if (params.nearCriticalDays !== undefined) qs.set('nearCriticalDays', String(params.nearCriticalDays));
    if (params.sort) qs.set('sort', params.sort);
    if (params.dir) qs.set('dir', params.dir);
    return fetchJson<TasksResponse>(`/api/v1/schedules/tasks?${qs.toString()}`);
  },

  /**
   * Which analysis rules this project has switched off (desktop "rules run for
   * this load"). Returns the whole catalog with a `suppressible` flag, because
   * suppressibility is a property of the RULE — only findings rules qualify —
   * and a client cannot work it out from the selection alone.
   */
  ruleSelection: (userProjectId: string) =>
    fetchJson<RuleSelectionResponse>(
      `/api/v1/analysis/rule-selection?userProjectId=${encodeURIComponent(userProjectId)}`,
    ),

  /**
   * Replaces the selection wholesale — the screen sends what it is showing.
   * Ids that cannot be honoured come back under `rejected` rather than being
   * dropped, so a save that stored less than it was sent can say so.
   */
  putRuleSelection: (userProjectId: string, suppressed: number[]) =>
    fetchJson<RuleSelectionSaved>(
      `/api/v1/analysis/rule-selection?userProjectId=${encodeURIComponent(userProjectId)}`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ suppressed }) },
    ),

  /**
   * S-curve — cumulative planned / earned / actual / forecast cost on one axis
   * (desktop SCurveScreen). Same addressing and same baseline rule as the WBS
   * rollup: omitting baselineLoadId uses the project's earliest version, and a
   * single-version project self-baselines. `granularityDays` is the bucket
   * width; 7 (weekly) is the desktop's.
   */
  sCurve: (params: {
    userProjectId?: string;
    filePath?: string;
    loadId?: string;
    baselineLoadId?: string;
    granularityDays?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.userProjectId) qs.set('userProjectId', params.userProjectId);
    if (params.filePath) qs.set('filePath', params.filePath);
    if (params.loadId) qs.set('loadId', params.loadId);
    if (params.baselineLoadId) qs.set('baselineLoadId', params.baselineLoadId);
    if (params.granularityDays != null) qs.set('granularityDays', String(params.granularityDays));
    return fetchJson<SCurveResponse>(`/api/v1/schedules/s-curve?${qs.toString()}`);
  },

  /**
   * Resource Usage — per-resource quantity variance in hours (desktop RESScreen).
   *
   * Needs a baseline, unlike `tasks`: the figure is earned hours against
   * budgeted hours SCHEDULED as at the data date. Omitting baselineLoadId lets
   * the server pick the project's earliest version, which is what the desktop
   * does; a single-version project self-baselines.
   */
  resourceUsage: (params: {
    userProjectId?: string;
    filePath?: string;
    loadId?: string;
    baselineLoadId?: string;
    /** Hours of variance to treat as on-plan. The desktop's "Margin (Hrs)". */
    threshold?: number;
    rsrcIds?: number[];
    wbsIds?: number[];
  }) => {
    const qs = new URLSearchParams();
    if (params.userProjectId) qs.set('userProjectId', params.userProjectId);
    if (params.filePath) qs.set('filePath', params.filePath);
    if (params.loadId) qs.set('loadId', params.loadId);
    if (params.baselineLoadId) qs.set('baselineLoadId', params.baselineLoadId);
    if (params.threshold !== undefined) qs.set('threshold', String(params.threshold));
    if (params.rsrcIds?.length) qs.set('rsrcIds', params.rsrcIds.join(','));
    if (params.wbsIds?.length) qs.set('wbsIds', params.wbsIds.join(','));
    return fetchJson<ResourceUsageResponse>(`/api/v1/schedules/resources?${qs.toString()}`);
  },

  /**
   * WBS rollup — the desktop WBSRollupView. Every activity under a package AND
   * its descendants, rolled into one set of dates and costs, with the package's
   * % complete set against how complete the baseline said it would be by the
   * data date. Re-parses, like `tasks` and `resources`, because the comparison
   * needs the calendar engine and the baseline pairing.
   */
  wbsRollup: (params: {
    userProjectId?: string;
    filePath?: string;
    loadId?: string;
    baselineLoadId?: string;
    percentRule?: WbsPercentRule;
    includeTasks?: boolean;
  }) => {
    const qs = new URLSearchParams();
    if (params.userProjectId) qs.set('userProjectId', params.userProjectId);
    if (params.filePath) qs.set('filePath', params.filePath);
    if (params.loadId) qs.set('loadId', params.loadId);
    if (params.baselineLoadId) qs.set('baselineLoadId', params.baselineLoadId);
    if (params.percentRule) qs.set('percentRule', params.percentRule);
    if (params.includeTasks !== undefined) qs.set('includeTasks', String(params.includeTasks));
    return fetchJson<WbsRollupResponse>(`/api/v1/schedules/wbs-rollup?${qs.toString()}`);
  },

  /**
   * Milestone trend analysis — each milestone's forecast finish across every
   * persisted version (desktop MTA). Project-level and read-only: it reads the
   * persisted TASK rows, so it costs one query however many versions exist.
   */
  milestoneTrend: (userProjectId: string, params: {
    incompleteOnly?: boolean;
    excludeCompletedBeforeBaseline?: boolean;
    includeZeroDuration?: boolean;
    taskCodes?: string[];
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.incompleteOnly !== undefined) qs.set('incompleteOnly', String(params.incompleteOnly));
    if (params.excludeCompletedBeforeBaseline !== undefined) {
      qs.set('excludeCompletedBeforeBaseline', String(params.excludeCompletedBeforeBaseline));
    }
    if (params.includeZeroDuration !== undefined) qs.set('includeZeroDuration', String(params.includeZeroDuration));
    if (params.taskCodes?.length) qs.set('taskCodes', params.taskCodes.join(','));
    const q = qs.toString();
    return fetchJson<MilestoneTrendResponse>(
      `/api/v1/projects/${encodeURIComponent(userProjectId)}/milestone-trend${q ? `?${q}` : ''}`,
    );
  },

  delayWindow: (basePath: string, revPath: string) =>
    fetchJson<DelayWindowResponse>('/api/v1/delay/windows', {
      method: 'POST',
      body: JSON.stringify({ basePath, revPath }),
    }),

  /** Multi-window chain (F4a). Files any order — server sorts by data date. */
  delayChain: (filePaths: string[], classification?: DelayClassification, grantedSpans?: DateSpan[]) =>
    fetchJson<DelayChainResponse>('/api/v1/delay/chain', {
      method: 'POST',
      body: JSON.stringify({ filePaths, classification, grantedSpans }),
    }),

  /** Persisted delay history for a project (F4a timeline). */
  delayHistory: (userProjectId: string) =>
    fetchJson<DelayHistoryResponse>(`/api/v1/delay/projects/${userProjectId}/history`),

  /**
   * UXR-2: re-derives a project's whole delay chain from the paths the SERVER
   * recorded at import, and returns each window's entitlement day matrix. No
   * filePaths — a project-first client has none, and the entitlement lattice
   * needs a re-parse (it reads the task graph and calendars, not the persisted
   * DelayEvent rows). Optional classification/grantedSpans override the
   * project's persisted delay-config for this run only.
   *
   * 409 SourcePathsUnavailable when the server no longer holds every version's
   * file — the error names the files, and the loads list's `reanalyzable` flag
   * lets the UI predict it before offering the action.
   *
   * Runs on the QUEUE (postAndAwait): this parses and re-schedules every
   * version of the project, which used to happen in the API process and left
   * every other request on the page waiting behind it.
   */
  delayReanalyze: (
    userProjectId: string,
    opts: { classification?: DelayClassification; grantedSpans?: DateSpan[] } = {},
  ) =>
    postAndAwait<DelayReanalyzeResponse>(`/api/v1/delay/projects/${userProjectId}/reanalyze`, opts),

  /**
   * Recommended unimpacted (progress-source) schedule for TIA (F4c).
   *
   * Pass `userProjectId` and the candidates are the project's own versions;
   * the reply then identifies them by loadId and never contains a server path.
   * `filePaths` is the legacy file-first form and gets paths echoed back.
   */
  recommendUnimpacted: (
    filePaths: string[] | null,
    events: { ocurredAt: string }[],
    opts: { maxDaysGap?: number; userProjectId?: string } = {},
  ) =>
    fetchJson<RecommendUnimpactedResponse>('/api/v1/delay/tia/recommend-unimpacted', {
      method: 'POST',
      body: JSON.stringify({ filePaths: filePaths ?? undefined, events, ...opts }),
    }),

  /**
   * Time Impact Analysis (F4c). Name the unimpacted schedule either by path
   * (legacy) or by project + loadId.
   */
  delayTia: (
    unimpactedPath: string | null,
    events: TiaEventInput[],
    opts: { userProjectId?: string; loadId?: string } = {},
  ) =>
    fetchJson<TiaResponse>('/api/v1/delay/tia', {
      method: 'POST',
      body: JSON.stringify({ unimpactedPath: unimpactedPath ?? undefined, events, ...opts }),
    }),

  /**
   * ClaimCracker AI report (C4). filePaths are oldest -> newest (first is the
   * baseline). Returns 503 when the server has no ANTHROPIC_API_KEY configured.
   * userProjectId (UX3): when supplied, the server falls back to the
   * project's persisted delay-config for classification/concurrency (D6/M7)
   * and attaches its admitted-evidence exhibit list for citation (E6).
   */
  claimReport: (filePaths: string[] | null, userProject?: UserProjectSettings, userProjectId?: string) =>
    fetchJson<ClaimReportResponse>('/api/v1/reports/claim', {
      method: 'POST',
      body: JSON.stringify({ filePaths: filePaths ?? undefined, userProject, userProjectId }),
    }),

  /**
   * F2b (enhancement-queue #3, 2026-07-13): same report as claimReport
   * above, rendered server-side as a .docx. Bypasses fetchJson (the response
   * is a binary blob, not JSON) — same request body/gating as claimReport.
   */
  claimReportDocx: (filePaths: string[] | null, userProject?: UserProjectSettings, userProjectId?: string) =>
    claimReportBinary('docx', filePaths, userProject, userProjectId),

  /** F2b: the same report rendered server-side as a PDF — the format a tribunal receives. */
  claimReportPdf: (filePaths: string[] | null, userProject?: UserProjectSettings, userProjectId?: string) =>
    claimReportBinary('pdf', filePaths, userProject, userProjectId),

  // ── Enhancement-queue #2 (2026-07-08 eval): project persistence UX ──────
  // The backend already had persisted snapshot/trend reads; these are the
  // list/create/analyze/history calls that were missing any UI at all.

  /** List the current user's persisted projects, most-recently-updated first. */
  listProjects: () => fetchJson<ProjectListResponse>('/api/v1/projects'),

  /** Self-service create — no externalId, unlike the VistraPM pairing flow. */
  createProject: (name: string) =>
    fetchJson<PairedProjectResponse>('/api/v1/projects', { method: 'POST', body: JSON.stringify({ name }) }),

  /** Parses + schedules + runs rules + persists a Load into a project, synchronously. */
  analyzeIntoProject: (userProjectId: string, filePath: string, userProject?: UserProjectSettings) =>
    fetchJson<AnalysisResultResponse>(`/api/v1/projects/${userProjectId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ filePath, userProject }),
    }),

  /**
   * M6 QSRA. Project-first: the server resolves which version to run, so no
   * path crosses the wire in either direction.
   *
   * Genuinely slow — a thousand iterations is a thousand full forward passes
   * over the network, tens of seconds on a real schedule — and, unlike the
   * chain run, it never awaits anything while it works. Run in the API process
   * it blocked every other request for its whole duration; it runs on the
   * QUEUE (postAndAwait) for that reason.
   */
  qsra: (userProjectId: string, loadId: string | null, opts: QsraRequest = {}) =>
    postAndAwait<QsraResponse>('/api/v1/reports/qsra', {
      userProjectId, loadId: loadId ?? undefined, ...opts,
    }),

  /**
   * M5 — the project's EVM roll-up over the latest persisted load. Reads
   * persisted task fields only; it does not re-run the engine.
   */
  projectEvm: (userProjectId: string) =>
    fetchJson<ProjectEvmResponse>(`/api/v1/projects/${userProjectId}/evm`),

  /**
   * F5 cross-load over a project's whole chain. EXPENSIVE — it re-parses and
   * re-schedules every version in the project, so it is called on demand from
   * a button, never on page load.
   */
  crossLoad: (userProjectId: string) =>
    fetchJson<CrossLoadResponse>('/api/v1/reports/cross-load', {
      method: 'POST',
      body: JSON.stringify({ userProjectId }),
    }),

  /**
   * M3 — every persisted load's KPI rows for a project, flattened. The URL is
   * also what a BI connector points at, so it is built the same way here and
   * shown to the user (see kpiExportUrl).
   */
  kpiExport: (userProjectId: string) =>
    fetchJson<KpiExportResponse>(`/api/v1/projects/${userProjectId}/kpis/export`),

  /**
   * A signed, read-only export URL for a BI tool, which cannot sign in. The
   * server returns a path; kpiExportUrl makes it absolute.
   */
  kpiExportLink: (userProjectId: string) =>
    fetchJson<{ path: string; expiresAt: string }>(`/api/v1/projects/${userProjectId}/kpis/export-link`, {
      method: 'POST',
    }),

  /**
   * B4 signup. Unauthenticated on the server by necessity; still routed through
   * fetchJson so the base URL and error decoding are the app's, not ad hoc.
   */
  startTrial: (email: string) =>
    fetchJson<StartTrialResponse>('/billing/signup/start', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * The signed-in person's own subscription. `fresh` skips the server's short
   * cache — used right after returning from Stripe Checkout.
   */
  billingMe: (fresh = false) => fetchJson<BillingStatus>(`/api/v1/billing/me${fresh ? '?fresh=1' : ''}`),

  /** A Stripe Checkout for the card-required free trial. */
  startCheckout: () => fetchJson<{ url: string }>('/api/v1/billing/checkout', { method: 'POST' }),

  /** The Stripe Customer Portal: card, invoices, cancellation. */
  openBillingPortal: () => fetchJson<{ url: string }>('/api/v1/billing/portal', { method: 'POST' }),

  /** B4: "is my tenant ready yet?" — reads only what the WEBHOOK wrote. */
  trialStatus: (shortId: string) =>
    fetchJson<TrialStatusResponse>(`/billing/signup/status?tenant=${encodeURIComponent(shortId)}`),

  /** B1b operator console: every tenant with its billing state and licence summary. */
  adminTenants: () => fetchJson<AdminTenantListResponse>('/api/v1/admin/tenants'),

  /** B1b: run the reconcile pass now — the whole estate, or one tenant. */
  reconcile: (shortId?: string) =>
    fetchJson<ReconcileRunResponse>(
      `/api/v1/admin/billing/reconcile${shortId ? `?shortId=${encodeURIComponent(shortId)}` : ''}`,
      { method: 'POST' },
    ),

  /** Every persisted load for a project, oldest first. */
  projectLoads: (userProjectId: string) =>
    fetchJson<ProjectLoadListResponse>(`/api/v1/projects/${userProjectId}/loads`),

  /**
   * A project's snapshot — health score, task counts, KPI rows. Defaults to the
   * latest version; pass `loadId` to pin it to one node on the spine, same
   * convention as `activities` and `sCurves`. A loadId from another project is
   * a 404, not a quiet fall back to the latest.
   */
  projectKpis: (userProjectId: string, loadId?: string) =>
    fetchJson<ProjectKpiSnapshot>(
      `/api/v1/projects/${userProjectId}/kpis${loadId ? `?loadId=${encodeURIComponent(loadId)}` : ''}`,
    ),

  /** One rule's output value across every persisted load for a project — the trend-chart series. */
  kpiTrend: (userProjectId: string, ruleId: number) =>
    fetchJson<KpiTrendResponse>(`/api/v1/projects/${userProjectId}/kpis/trend?ruleId=${ruleId}`),

  // ── Enhancement-queue #5 (2026-07-08 eval): evidence/case UI ────────────
  // E5's case-timeline/exhibits/notice-compliance and E1's connector health
  // panel already had backend endpoints (case-timeline.ts, connectors.ts)
  // with zero web UI — these are the calls the new Evidence page uses.

  /** E5 merged chronology of persisted delay/acceleration events + admitted evidence. */
  caseTimeline: (userProjectId: string, opts: { fromDate?: string; toDate?: string; delayEventId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.fromDate) qs.set('fromDate', opts.fromDate);
    if (opts.toDate) qs.set('toDate', opts.toDate);
    if (opts.delayEventId) qs.set('delayEventId', opts.delayEventId);
    const q = qs.toString();
    return fetchJson<CaseTimelineResponse>(`/api/v1/projects/${userProjectId}/timeline${q ? `?${q}` : ''}`);
  },

  /** E5 numbered, stable exhibit list of admitted evidence — optionally restricted to specific delay events. */
  exhibitList: (userProjectId: string, delayEventIds?: string[]) => {
    const qs = delayEventIds && delayEventIds.length > 0 ? `?delayEventIds=${delayEventIds.map(encodeURIComponent).join(',')}` : '';
    return fetchJson<ExhibitListResponse>(`/api/v1/projects/${userProjectId}/exhibits${qs}`);
  },

  /**
   * E6-full part 2 — admit an item into the Evidence Vault with its excerpts.
   * Citability is set HERE and nowhere else: admitted items are immutable
   * (capture-service.ts exports no update path), so an excerpt left non-citable
   * stays non-quotable for the life of that item.
   */
  admitEvidence: (userProjectId: string, body: AdmitEvidenceRequest) =>
    fetchJson<AdmitEvidenceResponse>(`/api/v1/projects/${userProjectId}/evidence`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** E6-full part 2 — admitted items with ALL excerpts (analyst view; the report path sees citable only). */
  evidenceItems: (userProjectId: string) =>
    fetchJson<EvidenceItemsResponse>(`/api/v1/projects/${userProjectId}/evidence`),

  /** E5 time-bar / notice-compliance report for every persisted delay event in the project. */
  noticeCompliance: (userProjectId: string, noticeClauseDays: number) =>
    fetchJson<NoticeComplianceResponse>(`/api/v1/projects/${userProjectId}/notice-compliance?noticeClauseDays=${noticeClauseDays}`),

  /** E1 connector health-panel listing — never returns decrypted tokens. */
  connectorAccounts: (userProjectId: string) =>
    fetchJson<ConnectorAccountsResponse>(`/api/v1/connectors?userProjectId=${encodeURIComponent(userProjectId)}`),

  /** E1 builds the provider's OAuth authorization URL. Rejects with a 503 if that provider's OAuth app isn't configured yet (env-driven, see connectors/types.ts ConnectorNotConfiguredError). */
  connectorAuthUrl: (provider: string, userProjectId: string, accountEmail: string) =>
    fetchJson<{ url: string }>(
      `/api/v1/connectors/${encodeURIComponent(provider)}/auth-url?userProjectId=${encodeURIComponent(userProjectId)}&accountEmail=${encodeURIComponent(accountEmail)}`,
    ),

  /** E1 revokes (best-effort, provider-side) and marks a connector account revoked locally. */
  disconnectAccount: (id: string) => fetchJson<void>(`/api/v1/connectors/${id}`, { method: 'DELETE' }),

  // ── Enhancement-queue #3 (2026-07-08 eval): claims workspace UX2/UX3 ────
  // D6's persisted delay-config (classification/grantedSpans/concurrency
  // ruleset/float ownership) already had GET/PUT endpoints with zero UI —
  // every module either passed classification inline per-request or ignored
  // the persisted default. This is the dedicated Contract "Framing &
  // Classification" editor (UX2) that manages it.

  /** D6 persisted delay-analysis defaults for a project. Empty defaults (not 404) when never set. */
  getDelayConfig: (userProjectId: string) => fetchJson<DelayConfigResponse>(`/api/v1/projects/${userProjectId}/delay-config`),

  /** D6 replaces the project's delay-config wholesale (not a partial patch). */
  putDelayConfig: (userProjectId: string, config: DelayConfig) =>
    fetchJson<DelayConfigResponse>(`/api/v1/projects/${userProjectId}/delay-config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
};

export interface ClaimReportResponse {
  schemaVersion: string;
  markdown: string;
  payload: unknown;
  validation: { passed: boolean; violations: string[]; retried: boolean };
}

export interface DelayEventRow {
  eventType: number; // 40 = Delay, 50 = Acceleration
  sourceTaskCd: string;
  fragnetType: string; // 'Start' | 'Duration'
  ocurredAt: string;
  endedAt: string;
  delayEventDurationHrs: number;
  delayEventDurationDays: number;
  description: string;
}

export interface DelayWindowResponse {
  schemaVersion: string;
  window: { baseFile: string; baseDataDate: string | null; revFile: string; revDataDate: string | null };
  projectFloatDays: number | null;
  totals: { delayDays: number; accelerationDays: number };
  events: DelayEventRow[];
}

export type DelayClassification = Record<string, 'owner' | 'contractor' | 'forceMajeure'>;
export interface DateSpan { from: string; to: string }

export interface EntitlementDay {
  date: string;
  fault: string;
  isDelay: boolean;
  isAccel: boolean;
  taskCds: string[];
  /**
   * M7's per-day candidate-concurrency note, present only on Concurrent days.
   * The API has always sent it (routes/delay.ts serializeEntitlement); it was
   * missing from this type, so no screen could show it.
   */
  note?: string;
}
export interface EntitlementSummary {
  ownerFault: number;
  contractorFault: number;
  forceMajeure: number;
  concurrent: number;
  totalDelays: number;
  ruledCompExtensionDays: number;
  ruledNonCompExtensionDays: number;
  ruledNonCompDelay: number;
  daysEvaluated: number;
  asOfDataDate: string | null;
}
/**
 * One window's entitlement result. `concurrencyRuleset` and `disclaimer` are
 * part of M7's disclosure contract — the summary must never be shown without
 * the ruleset it was computed under and the standing disclaimer verbatim, so
 * they are required here rather than optional.
 */
export interface WindowEntitlement {
  summary: EntitlementSummary;
  delayBalanceHrs: number | null;
  concurrencyRuleset: 'malmaison' | 'contract-clause-bars-eot';
  disclaimer: string;
  days: EntitlementDay[];
}

export interface ChainWindow {
  baseFile: string;
  baseDataDate: string | null;
  revFile: string;
  revDataDate: string | null;
  projectFloatDays: number | null;
  totals: { delayDays: number; accelerationDays: number };
  events: DelayEventRow[];
  entitlement?: WindowEntitlement | null;
  /** Set only on a persisting run — the same load-identity key the spine binds on. */
  baseLoadId?: string;
  revLoadId?: string;
}
export interface DelayChainResponse {
  schemaVersion: string;
  persisted: boolean;
  windows: ChainWindow[];
  totals: { delayDays: number; accelerationDays: number };
  /**
   * B3: present when the answer was TRIMMED by the caller's plan. The server
   * sends it because a truncated result that looks complete is worse than no
   * result — the UI must say what was withheld rather than imply this is all
   * there is.
   */
  planNotice?: string;
}

/** Response of POST /delay/projects/:id/reanalyze — a persisting chain run. */
export interface DelayReanalyzeResponse {
  schemaVersion: string;
  userProjectId: string;
  persisted: true;
  windowCount: number;
  windows: ChainWindow[];
  totals: { delayDays: number; accelerationDays: number };
}

/**
 * One persisted delay/acceleration event. Previously typed with only a subset of
 * the fields the endpoint actually returns; the load ids and data dates were
 * always in the payload (repos/delay-repo.ts DelayHistoryEntry) and are what
 * UXR-1's spine binds windows on — see newapp/spine-binding.ts.
 */
export interface DelayHistoryEvent {
  id: string;
  eventType: number;
  sourceTaskCd: string;
  /**
   * 'Start' | 'Duration' — a STRING, not a number: DelayEvent.FragnetType is a
   * VarChar(20) and delay-repo.ts passes it through verbatim. Typed as a number
   * when this interface was widened for UXR-1, which nothing caught because no
   * screen rendered the field until UXR-2's event table did.
   */
  fragnetType: string;
  ocurredAt: string;
  endedAt: string;
  /** SIGNED: negative means acceleration. */
  delayEventDurationHrs: number;
  /** SIGNED working days: negative means acceleration. */
  delayEventDurationDays: number;
  description: string | null;
  baseLoadId: string;
  baseFileName: string;
  baseDataDate: string | null;
  revLoadId: string;
  revFileName: string;
  revDataDate: string | null;
}

export interface DelayHistoryResponse {
  schemaVersion: string;
  userProjectId: string;
  /** Project-wide sums. accelerationDays is <= 0, since the durations are signed. */
  totals: { delayDays: number; accelerationDays: number };
  events: DelayHistoryEvent[];
  /**
   * B3: present when the plan trimmed the history to fewer windows than the
   * project has. Shown, never swallowed — a truncated forensic record that
   * looks complete is worse than none.
   */
  planNotice?: string;
}

export interface ProjectListEntry {
  userProjectId: string;
  externalId: string | null;
  name: string;
  createdAt: string;
  updatedAt: string;
  lastDataDate: string | null;
}

export interface ProjectListResponse {
  schemaVersion: string;
  projects: ProjectListEntry[];
}

export interface PairedProjectResponse {
  schemaVersion: string;
  userProjectId: string;
  externalId: string | null;
  name: string;
  created: boolean;
}

export interface AnalysisResultResponse {
  schemaVersion: string;
  outputCount: number;
  errorCount: number;
  durationMs: number;
  loadId?: string;
}

export interface ProjectLoadRow {
  loadId: string;
  fileName: string;
  dataDate: string | null;
  /**
   * The load's cached health score. null when that load was persisted before
   * scoring existed, or its cache is unreadable — so a grade badge must be
   * omitted, never rendered as a zero or an F.
   */
  healthScore: { score: number; letterGrade: string } | null;
  /**
   * Whether the server still holds this version's source file and can re-derive
   * analyses that need a re-parse. A capability bit, not a path — see the
   * Load.sourcePathStr comment in schema.prisma. Optional in the type because
   * older servers omit it; treat `undefined` as unknown, not false.
   */
  reanalyzable?: boolean;
}

export interface ProjectLoadListResponse {
  schemaVersion: string;
  userProjectId: string;
  loads: ProjectLoadRow[];
}

export interface KpiTrendPoint {
  loadId: string;
  fileName: string;
  dataDate: string | null;
  outputValue: string | null;
}

export interface KpiTrendResponse {
  schemaVersion: string;
  ruleId: number;
  name: string;
  dataType: string | null;
  formatString: string | null;
  points: KpiTrendPoint[];
}

/**
 * The persisted analysis snapshot — what dashboards read INSTEAD of re-parsing
 * (architecture-review-2026-07 priority #2).
 *
 * `calcFinish`, `lastAnalysisAt`, `sCurves` and `advice` were always in the
 * payload (routes/kpis.ts shapeSnapshot, cached at analysis time in
 * Load.outputCacheStr) but missing from this type, so no screen could reach
 * them — which is why the legacy Dashboard re-runs a full synchronous
 * `/reports/full` for figures the server already had. Typed optional because a
 * load persisted before the cache existed carries none of them.
 */
/**
 * One rule output row out of the persisted snapshot.
 *
 * The server has sent all of this since QA1; the type used to declare three of
 * the fifteen fields, so no screen could name the activities behind a finding,
 * show a before/after for a comparison rule, or format a value according to the
 * A8 typed-output contract. That is why the legacy Reports page can only show
 * rule-name and a count.
 */
export interface KpiRow {
  ruleId: number;
  /**
   * The RuleNameID enum member — the IDENTIFIER, frozen under kpi-catalog-1.
   * Screens show `displayName`; this stays the thing the S_/C_/D_ family badge
   * is derived from and the string an export round-trips on.
   */
  name: string;
  /** What the desktop app calls this rule, e.g. 'Activities with No Budget'. */
  displayName: string | null;
  /** The desktop's one-line explanation of the rule, where it has one. */
  description: string | null;
  /** Rule catalog metadata (C1-frozen), resolved server-side per row. */
  categories: string[];
  severityCategory: string | null;
  /** A8: how to read outputValue — Date | Cost | Percent | Count | Day | Hour | Decimal | Text | List | Chart. */
  dataType: string | null;
  /** .NET display format string that goes with dataType. */
  formatString: string | null;
  entityType: number;
  entityId1: number | null;
  entityId2: number | null;
  /** QA1: the activity code and description behind a Task-entity finding. */
  entityCode1: string | null;
  entityName1: string | null;
  entityCode2: string | null;
  entityName2: string | null;
  /** Comparison rules carry both sides; single-load rules leave these null. */
  beforeValue: string | null;
  outputValue: string | null;
  afterValue: string | null;
  /** The version a comparison row was measured against; null on single-load rows. */
  baseLoadId?: string | null;
}

export interface ProjectKpiSnapshot {
  schemaVersion: string;
  loadId: string;
  fileName: string;
  dataDate: string | null;
  calcFinish?: string | null;
  lastAnalysisAt?: string | null;
  taskCount: number;
  completedCount: number;
  incompleteCount: number;
  inProgressCount: number;
  /**
   * The WHOLE score, not just the headline. The persisted cache has always held
   * the component breakdown, the unported-rule warnings and the grade prose;
   * this type flattened it to two fields, which is why "why is this a C+?" had
   * no answer anywhere in the app.
   */
  healthScore: HealthScoreResult | null;
  sCurves?: SCurves | null;
  advice?: AdviceReport | null;
  kpis: KpiRow[];
}

export interface RecommendUnimpactedResponse {
  schemaVersion: string;
  earliestEventDate: string;
  /** filePath is present ONLY when the caller supplied paths — see rule 1 in project-files.ts. */
  recommended: {
    filePath?: string;
    loadId: string | null;
    fileName: string;
    dataDate: string | null;
    gapDays: number;
  } | null;
  requiresUnwind: boolean;
  reasons: string[];
  candidates: Array<{
    filePath?: string;
    loadId: string | null;
    fileName: string;
    dataDate: string | null;
    onOrBeforeEvent: boolean;
  }>;
}

/* --- M6 QSRA (Monte-Carlo schedule risk) ---------------------------------
   Mirrors src/risk/qsra-service.ts. Written against that file's exported
   interfaces field-for-field rather than against a sample response — the eight
   sent-but-untyped defects this file has carried were all hand-written the
   other way round. */

export interface PercentileDate { p: number; date: string }

export interface CumulativePoint { date: string; cumulativeProbability: number }

export interface HistogramBin { binStartMs: number; binEndMs: number; count: number }

export interface QsraTargetResult {
  id: string;
  label: string;
  /** The single date the CPM engine produced — the one already in the schedule. */
  deterministicDate: string;
  meanDate: string;
  stdevDays: number;
  percentiles: PercentileDate[];
  /** Share of iterations that finished on or before the deterministic date. */
  probabilityMeetDeterministic: number;
  /** null when the target carries no constraint to measure against. */
  probabilityMeetMustFinish: number | null;
  histogram: HistogramBin[];
  cumulativeCurve: CumulativePoint[];
}

export interface QsraUncertainty {
  /** Activities whose remaining duration the sampler could vary. */
  sampledActivities: number;
  /** Activities eligible to be sampled, whether or not a range resolved for them. */
  candidateActivities: number;
  /** True when nothing varied between iterations: no sampled activity and no risk event. */
  degenerate: boolean;
  /** Plain-language explanation when `degenerate`; null otherwise. */
  note: string | null;
}

export interface CriticalityEntry {
  taskCode: string;
  name: string | null;
  /** Share of iterations in which this activity landed on the critical path. */
  criticalityIndex: number;
}

/** M6 R3c — one tornado entry; Spearman rank correlation against the finish. */
export interface SensitivityEntry { taskCode: string; spearman: number }

export interface QsraConvergenceResult {
  converged: boolean;
  iterationsRun: number;
  achievedDeltaHours: number;
}

export interface QsraMethodology {
  seed: number;
  iterations: number;
  distributions: string;
  /**
   * A GAO Schedule Assessment Guide disclosure, and CONDITIONAL — the engine
   * swaps the text when correlation is actually modelled. Render whatever
   * string arrives, verbatim; a hard-coded caveat would eventually assert
   * something the run did not do.
   */
  correlationCaveat: string;
}

export interface QsraResponse {
  schemaVersion: string;
  iterations: number;
  seed: number;
  targets: QsraTargetResult[];
  criticality: CriticalityEntry[];
  /**
   * How much uncertainty the run actually carried. Read `degenerate` BEFORE
   * quoting any percentile: a run where nothing resolved to a duration range
   * produces P10 = P50 = P90, zero spread, and a 100% probability of meeting
   * the date — the most confident-sounding output the product can produce, and
   * the one backed by no simulation at all.
   *
   * Optional so a response from an older API still parses.
   */
  uncertainty?: QsraUncertainty;
  methodology: QsraMethodology;
  iterationsPerSecond: number;
  sensitivity?: SensitivityEntry[];
  convergence?: QsraConvergenceResult;
  /**
   * B3: present when the answer was TRIMMED by the caller's plan. The server
   * sends it because a truncated result that looks complete is worse than no
   * result — the UI must say what was withheld rather than imply this is all
   * there is.
   */
  planNotice?: string;
  /** Which schedule produced this distribution. null loadId when a path was supplied. */
  loadId: string | null;
  fileName: string;
  dataDate: string | null;
}

export interface QsraRequest {
  iterations?: number;
  seed?: number;
  computeSensitivity?: boolean;
  sensitivityTopN?: number;
}

/* --- M5 EVM roll-up ------------------------------------------------------
   Mirrors src/reporting/evm-rollup.ts + the route's additions in
   routes/kpis.ts. Built 2026-08-17 (`053a3f87`) and bound to no client until
   the 2026-08-18 sweep found it. */

export interface ProjectEvmResponse {
  schemaVersion: string;
  loadId: string;
  dataDate: string | null;
  taskCount: number;

  /** Budget at completion — Σ baseline cost. */
  bac: number;
  /** Earned value (BCWP). */
  ev: number;
  /** Planned value (BCWS). */
  pv: number;
  /** Actual cost (ACWP). */
  ac: number;
  /** EV / PV; null when PV is 0. */
  spi: number | null;
  /** EV / AC; null when AC is 0. */
  cpi: number | null;
  /** EV − PV. */
  sv: number;
  /** EV − AC. */
  cv: number;
  /** Cost efficiency the remaining work needs; null when BAC = AC. */
  tcpi: number | null;
  /** Σ per-task EAC — the bottom-up figure, and the one VAC uses. */
  eac: number;
  /** BAC / CPI — an independent check reported for comparison, never used for VAC. */
  eacCpiMethod: number | null;
  /** BAC − EAC, from the bottom-up EAC. */
  vac: number;

  costLoadedTaskCount: number;
  skippedTaskCount: number;
  acReportedTaskCount: number;
  /**
   * FALSE when not one task reported actual cost — and this is the field that
   * has to be read before any of the cost variances above are shown. With AC
   * at zero, `cv` is `ev - 0` (a large FAVOURABLE variance) and `tcpi` is
   * comfortably under 1, so a project with no recorded cost progress reads as
   * substantially under budget. evm-rollup.ts verified that against the raw
   * XER and states plainly that consumers must suppress or qualify CV and TCPI
   * when this is false.
   */
  actualCostReported: boolean;
  /** True when any task carried a baseline cost at all. */
  costLoaded: boolean;
  /** The server's own words for the above; null when actual cost WAS reported. */
  actualCostNote: string | null;
  /** ES/AT/SPI(t) are not computed here — the route says where they live. */
  earnedSchedule: null;
  earnedScheduleNote: string;
}

/* --- F5 cross-load (Earned Schedule + trends over the whole chain) --------
   Mirrors src/rules/cross-load/cross-load-runner.ts. The one figure that
   cannot be had from a single version: where the value earned so far sits on
   the BASELINE's planned curve. */

export interface EarnedScheduleResult {
  /** Current project Earned Value driving the projection; null when uncosted. */
  earnedValue: number | null;
  /** Where cumulative baseline planned cost first reaches EV. null when not computable. */
  earnedScheduleDate: string | null;
  /** Which file was treated as the baseline — a contestable choice, so it is stated. */
  baselineFile: string;
}

export interface CrossLoadResponse {
  schemaVersion: string;
  loadCount: number;
  earnedSchedule: EarnedScheduleResult | null;
  /** File names in chain order, oldest first. Never paths. */
  files: string[];
}

/* --- M3 warehouse export -------------------------------------------------
   A star schema: `loads` is the dimension, `kpis` the fact table. Shaped for a
   Power BI JSON connector, which is why it is flat and repeats the load
   columns on every row. */

export interface KpiExportLoad {
  loadId: string;
  fileName: string;
  dataDate: string | null;
  calcFinish: string | null;
  taskCount: number;
  completedCount: number;
  incompleteCount: number;
  inProgressCount: number;
  healthScore: number | null;
  letterGrade: string | null;
}

export interface KpiExportRow {
  loadId: string;
  fileName: string;
  dataDate: string | null;
  ruleId: number;
  name: string;
  /** What the desktop app calls this rule; null when the catalog has no entry. */
  displayName: string | null;
  description: string | null;
  categories: string[];
  severityCategory: number | null;
  dataType: string | null;
  formatString: string | null;
  entityType: number;
  entityId1: number | null;
  entityId2: number | null;
  entityCode1: string | null;
  entityName1: string | null;
  entityCode2: string | null;
  entityName2: string | null;
  beforeValue: string | null;
  outputValue: string | null;
  afterValue: string | null;
}

export interface KpiExportResponse {
  schemaVersion: string;
  userProjectId: string;
  loads: KpiExportLoad[];
  kpis: KpiExportRow[];
}

export interface TiaEventInput {
  code: string;
  name?: string;
  ocurredAt: string;
  endedAt: string;
  fragnetType?: 'Start' | 'Duration';
  sourceTaskCd?: string;
  impactedTasks?: { taskCode: string; predRel?: 'FS' | 'SS' | 'FF' | 'SF' }[];
}
export interface TiaResponse {
  schemaVersion: string;
  unimpactedFile: string;
  /** Which version was used, when it was named by identity rather than path. */
  unimpactedLoadId: string | null;
  dataDate: string | null;
  preDelayCalcFinish: string | null;
  postDelayCalcFinish: string | null;
  resultingDelayDays: number;
  injectedTaskCodes: string[];
  taskImpacts: Array<{ taskCode: string; preStart: string | null; postStart: string | null; daysDelayed: number | null }>;
}

export interface TaskRow {
  task_id: number;
  tsk_cd: string | null;
  name: string | null;
  task_type: string | null;
  status_cd: string | null;
  early_start: string | null;
  early_finish: string | null;
  late_start: string | null;
  late_finish: string | null;
  actual_start: string | null;
  actual_finish: string | null;
  /**
   * P6's trgt_strt_dt / trgt_end_dt — the BASELINE dates. The server has always
   * sent these; the type omitted them, which is why no screen could show
   * planned-vs-current, the one comparison a delay analyst reaches for first.
   */
  target_start: string | null;
  target_finish: string | null;
  total_float_days: number | null;
  total_float_hrs: number;
  free_float_hrs: number;
  duration_days: number | null;
  /** Also always sent, also never typed. */
  actual_duration_days: number | null;
  remaining_days: number | null;
  comp_pct: number;
  is_loe: boolean | null;
  wbs_name: string | null;
  cstr_type_1: string | null;
  cstr_dt_1: string | null;
  pred_count: number;
  succ_count: number;
  /** Both computed server-side from the request's criticalDays/nearCriticalDays. */
  is_critical: boolean;
  is_near_critical: boolean;
  is_driving: boolean;
}

/** Columns the server can sort on. Kept in step with the route's enum. */
export type TaskSortKey =
  | 'tsk_cd' | 'name' | 'status_cd' | 'task_type'
  | 'duration_days' | 'remaining_days'
  | 'early_start' | 'early_finish' | 'target_finish'
  | 'total_float_days' | 'comp_pct';

export interface TasksResponse {
  total: number;
  offset: number;
  limit: number;
  /** Which schedule this grid is. null for a legacy filePath-addressed request. */
  loadId: string | null;
  fileName: string;
  dataDate: string | null;
  /**
   * Earliest start and latest finish across the whole FILTERED set, current and
   * baseline dates both. The Gantt's time axis comes from here rather than from
   * the page in view, so paging forward slides the window instead of rescaling
   * it. Optional because the legacy filePath-addressed caller predates it.
   */
  span?: { firstStart: string | null; lastFinish: string | null };
  /**
   * Dependency links with BOTH ends on this page — everything the Gantt can
   * draw without pointing at a row that is not there. Subtract the per-task
   * counts here from `pred_count` / `succ_count` to get what is not shown.
   * Optional because the legacy filePath-addressed caller predates it.
   */
  rels?: TaskRel[];
  tasks: TaskRow[];
}

export interface RuleSelectionRow {
  id: number;
  name: string;
  /**
   * What the desktop app calls this rule. Null only for a rule the desktop
   * catalog has no entry for; render ruleLabel(name) in that case.
   */
  displayName: string | null;
  /** The desktop's one-line explanation of the rule, where it has one. */
  description: string | null;
  outputType: number;
  categories: string[];
  severityCategory: { name: string } | null;
  /** Only findings rules may be switched off — KPI rules are read by id elsewhere. */
  suppressible: boolean;
  suppressed: boolean;
}

export interface RuleSelectionResponse {
  schemaVersion: string;
  userProjectId: string;
  suppressed: number[];
  rules: RuleSelectionRow[];
}

export interface RuleSelectionSaved {
  schemaVersion: string;
  userProjectId: string;
  suppressed: number[];
  rejected: { notSuppressible: number[]; unknown: number[] };
}

export interface SCurveRow {
  /** Bucket date, ISO. Every series is reported against these and only these. */
  date: string;
  planned: number | null;
  earned: number | null;
  actual: number | null;
  forecast: number | null;
}

export interface SCurveResponse {
  schemaVersion: string;
  loadId: string | null;
  fileName: string;
  dataDate: string | null;
  baseline: { loadId: string; fileName: string; dataDate: string | null } | null;
  granularityDays: number;
  /**
   * The four curves on one set of buckets, each carried across weeks it did not
   * move and null outside its own range. A cumulative total does not go away
   * when nothing happens, so a hole here would break the line rather than
   * describe a quiet week.
   */
  rows: SCurveRow[];
  totals: {
    plannedAtEnd: number | null;
    forecastAtEnd: number | null;
    plannedToDate: number | null;
    earnedToDate: number | null;
    actualToDate: number | null;
  };
  /** Budget that never reaches the planned curve, when there is any. */
  omitted: {
    amount: number;
    taskCount: number;
    invertedRangeTaskCount: number;
    missingDatesTaskCount: number;
  } | null;
  /** False when the schedule carries no cost at all — a state, not an error. */
  costLoaded: boolean;
}

/** One dependency link, predecessor → successor. */
export interface TaskRel {
  pred: number;
  succ: number;
  /** FS, SS, FF, SF — or None where the file gave no type. */
  type: string;
  lag_days: number | null;
  /**
   * The link has no free float of its own, so it controls the successor's
   * dates. Computed by the backward pass, not read from P6's `drv_status`,
   * which is empty in every file in the corpus.
   */
  driving: boolean;
  /** Driving, and both ends critical: the chain carrying the end date. */
  critical: boolean;
}

export type UsageBucket = 'deficit' | 'overage' | 'acceptable';

export interface ResourceActivityRow {
  taskId: number;
  taskCode: string | null;
  name: string | null;
  totalFloatDays: number | null;
  /** RQtyBQWS — budgeted hours SCHEDULED by the data date. */
  scheduled: number;
  earned: number;
  sv: number;
  cv: number;
  compPct: number;
}

export interface ResourceUsageRow {
  rsrcId: number;
  name: string | null;
  shortName: string | null;
  type: string | null;
  sv: number;
  bqws: number;
  earned: number;
  actual: number;
  remaining: number;
  /** null, not 0, when the divisor is zero — see the route. */
  spi: number | null;
  cpi: number | null;
  assignments: number;
  bucket: UsageBucket;
  /** Capped at 200, worst variance first; activityCount is the true total. */
  activities: ResourceActivityRow[];
  activityCount: number;
}

export interface ResourceUsageResponse {
  loadId: string | null;
  fileName: string;
  dataDate: string | null;
  /** null means the load was self-baselined (single version, or a filePath caller). */
  baseline: { loadId: string; fileName: string; dataDate: string | null } | null;
  /** False when the schedule carries no resources at all — not the same as no variances. */
  resourceLoaded: boolean;
  threshold: number;
  totals: { sv: number; bqws: number; earned: number; actual: number; remaining: number };
  counts: Record<UsageBucket, number>;
  resources: ResourceUsageRow[];
  /** What the desktop's scope filter excluded, so the total can be reconciled. */
  scope: {
    assignmentsInScope: number;
    excludedCompleteActivity: number;
    excludedNotStarted: number;
    excludedNoBaseline: number;
  };
}

// ── WBS rollup (desktop WBSRollupView) ────────────────────────────────────

/** Which "% complete" definition drives the rollup. */
export type WbsPercentRule = 'performance' | 'schedule' | 'cost' | 'duration' | 'activities';

export type WbsProgressStatus =
  | 'ahead' | 'behind' | 'complete' | 'asExpected'
  | 'wbsNotInBase' | 'taskNotInBase' | 'unknown';

export interface WbsRollupTotals {
  startDate: string | null;
  finishDate: string | null;
  actualCost: number;
  eac: number;
  earnedValue: number;
  bcws: number;
  bac: number;
}

export interface WbsRollupRow {
  /** `w:<wbs_id>` or `t:<task_id>` — prefixed so the two id spaces cannot collide. */
  entryId: string;
  parentId: string | null;
  depth: number;
  kind: 'wbs' | 'task';
  code: string | null;
  description: string | null;
  blStart: string | null;
  blFinish: string | null;
  /** How complete the BASELINE said this would be by the data date. */
  blPlannedPct: number | null;
  curStart: string | null;
  curFinish: string | null;
  completePct: number | null;
  status: WbsProgressStatus;
  /** Activities rolled up, descendants included. Null on activity rows. */
  taskCount: number | null;
  /** Null on activity rows. */
  totals: WbsRollupTotals | null;
}

export interface WbsRollupResponse {
  loadId: string | null;
  fileName: string;
  dataDate: string | null;
  /** null means the version was self-baselined. */
  baseline: { loadId: string; fileName: string; dataDate: string | null } | null;
  percentRule: WbsPercentRule;
  /** Why that rule — false means no activity carries a budget. */
  costLoaded: boolean;
  /** Depth-first: a parent always precedes its children. */
  rows: WbsRollupRow[];
  scope: {
    wbsCount: number;
    taskCount: number;
    wbsNotInBaseline: number;
    tasksNotInBaseline: number;
    emptyWbs: number;
    includeTasks: boolean;
  };
}

// ── Milestone trend analysis (desktop MTA) ────────────────────────────────

export interface MilestoneTrendPoint {
  loadId: string;
  fileName: string;
  dataDate: string | null;
  /** act_end_date ?? early_end_dt, as a naive local ISO string. */
  finish: string | null;
  actualFinish: string | null;
  /** Calendar days moved since the previous retained point; null on the first. */
  moveDays: number | null;
}

export type MilestoneDirection = 'slipping' | 'recovering' | 'flat' | 'single';

export interface MilestoneSeries {
  tskCd: string;
  name: string | null;
  label: string;
  taskType: string | null;
  wbsName: string | null;
  isMilestoneType: boolean;
  statusCd: string | null;
  complete: boolean;
  points: MilestoneTrendPoint[];
  firstFinish: string | null;
  currentFinish: string | null;
  actualFinish: string | null;
  /** Against the FIRST version, not a baseline — see the server module for why. */
  slipDays: number | null;
  worstMoveDays: number | null;
  direction: MilestoneDirection;
}

export interface MilestoneCandidate {
  tskCd: string;
  name: string | null;
  label: string;
  taskType: string | null;
  isMilestoneType: boolean;
  statusCd: string | null;
  finish: string | null;
  actualFinish: string | null;
  selected: boolean;
}

export interface MilestoneTrendResponse {
  userProjectId: string;
  versions: Array<{ loadId: string; fileName: string; dataDate: string | null }>;
  milestones: MilestoneSeries[];
  candidates: MilestoneCandidate[];
  options: {
    incompleteOnly: boolean;
    excludeCompletedBeforeBaseline: boolean;
    includeZeroDuration: boolean;
    taskCodes: string[] | null;
  };
  scope: {
    chartable: number;
    excludedComplete: number;
    excludedBeforeBaseline: number;
    excludedNotMilestoneType: number;
    collapsedCompletedPoints: number;
  };
}

// ── Enhancement-queue #5 (2026-07-08 eval): evidence/case UI types ─────────

export type TimelineEntryKind = 'delayEvent' | 'accelerationEvent' | 'matrixDay' | 'evidence';

export interface CaseTimelineEntry {
  date: string;
  kind: TimelineEntryKind;
  sourceId: string;
  summary: string;
  detail: unknown;
}

export interface CaseTimelineResponse {
  userProjectId: string;
  entries: CaseTimelineEntry[];
}

/** E6-full: a quotable passage of an exhibit. Only CITABLE ones appear in the exhibit list. */
export interface ExhibitExcerpt {
  excerptId: string;
  text: string;
  sha256: string;
  /**
   * When the excerpt row was created. Sent by timeline-service.ts and omitted
   * here until the 2026-08-18 sweep — the eighth field in this file that the
   * server had always been sending with nothing able to read it.
   */
  createdAt: string;
}

export interface ExhibitListEntry {
  exhibitNumber: number;
  evidenceItemId: string;
  sourceSystem: string;
  occurredAt: string;
  subject: string | null;
  sha256: string | null;
  /** E6-full: the passages a claim report is allowed to quote from this exhibit. */
  excerpts: ExhibitExcerpt[];
  /**
   * When the exhibit entered the record — NOT when the event happened, which is
   * `occurredAt`. In a claim those are different facts and both are contested:
   * an exhibit admitted after the notice deadline reads very differently from
   * one admitted before it. Sent all along, typed nowhere until now.
   */
  admittedAt: string;
}

/** E6-full part 2 — one excerpt as submitted at admission. */
export interface AdmitExcerpt {
  text: string;
  citable: boolean;
}

export interface AdmitEvidenceRequest {
  sourceSystem: 'gmail' | 'm365' | 'gdrive' | 'onedrive' | 'dropbox' | 'manual';
  sourceNativeId: string;
  itemType: 'email' | 'attachment' | 'file' | 'photo' | 'log';
  occurredAt: string;
  subject?: string;
  excerpt: string;
  excerpts?: AdmitExcerpt[];
  /** UTF-8 text of the item. Exactly one of content / contentBase64. */
  content?: string;
  contentBase64?: string;
  links?: Array<{ linkType: 'delayEvent' | 'activity' | 'matrixDay' | 'notice'; delayEventId?: string; taskCode?: string }>;
}

export interface AdmitEvidenceResponse {
  evidenceItemId: string;
  sha256: string | null;
  admittedAt: string;
  /** Read back from storage, so the text shown is the NORMALIZED text that quotations are matched against. */
  excerpts: Array<{ excerptId: string; text: string; sha256: string; citable: boolean }>;
  citableCount: number;
}

export interface EvidenceItemRow {
  evidenceItemId: string;
  sourceSystem: string;
  itemType: string;
  occurredAt: string;
  subject: string | null;
  excerpt: string | null;
  sha256: string | null;
  admittedAt: string;
  /** ALL excerpts, citable or not — this is the analyst's own view of the record. */
  excerpts: Array<{ excerptId: string; text: string; citable: boolean }>;
}

export interface EvidenceItemsResponse {
  userProjectId: string;
  items: EvidenceItemRow[];
}

export interface ExhibitListResponse {
  userProjectId: string;
  exhibits: ExhibitListEntry[];
}

export interface NoticeComplianceEntry {
  delayEventId: string;
  taskCode: string;
  eventDate: string;
  compliant: boolean | null;
  noticeFoundAt?: string;
  daysLate?: number;
}

export interface NoticeComplianceResponse {
  userProjectId: string;
  noticeClauseDays: number;
  report: NoticeComplianceEntry[];
}

export type ConnectorProvider = 'gmail' | 'm365' | 'gdrive' | 'onedrive' | 'dropbox';
export type ConnectorAccountStatus = 'connected' | 'expired' | 'revoked' | 'error';

export interface ConnectorAccountRow {
  id: string;
  userProjectId: string;
  provider: ConnectorProvider;
  accountEmail: string;
  scopes: string[];
  status: ConnectorAccountStatus;
  connectedAt: string;
  lastRefreshedAt: string | null;
  lastError?: string | null;
}

export interface ConnectorAccountsResponse {
  accounts: ConnectorAccountRow[];
}

// ── Enhancement-queue #3 (2026-07-08 eval): claims workspace types ─────────

export type FaultedPartyName = 'owner' | 'contractor' | 'forceMajeure';

export interface DelayConfigDateSpan { from: string; to: string }

export interface DelayConfig {
  classification: Record<string, FaultedPartyName>;
  grantedSpans: DelayConfigDateSpan[];
  tiaDefaults: { maxDaysGapForAutoUnimpacted: number };
  concurrencyRuleset: 'malmaison' | 'contract-clause-bars-eot';
  floatOwnership: 'project' | 'contractor';
}

export interface DelayConfigResponse extends DelayConfig {
  schemaVersion: string;
}

/** GET /api/v1/billing/me — the Billing page and the subscription gate. */
export interface BillingStatus {
  /** This deployment makes people subscribe before using the app. */
  required: boolean;
  /** Stripe is configured well enough to start a checkout. */
  available: boolean;
  /** Staff, demo or operator account: never asked to subscribe. */
  exempt: boolean;
  entitled: boolean;
  /** Stripe's subscription status: trialing, active, past_due, canceled, … */
  status: string | null;
  trialDays: number;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canManage: boolean;
  /** Minor units (cents), Stripe's own convention. */
  price: { amount: number; currency: string; interval: string; intervalCount: number } | null;
  /** starter | team | enterprise, from the subscribed Stripe product; null when untagged or none. */
  plan: string | null;
  /** Projects a paid plan allows; null is unlimited, or no subscription yet. */
  projectLimit: number | null;
}

/** B4 — what POST /billing/signup/start hands back. */
export interface StartTrialResponse {
  /** Stripe Checkout. The browser is sent here to enter a card. */
  checkoutUrl: string;
  /** The tenant handle the webhook will provision under; the completion page polls on it. */
  shortId: string;
}

/**
 * B4 — the completion page's poll.
 *
 * Deliberately carries NO credential: the endpoint is unauthenticated and
 * keyed only on a handle that travels in a URL.
 */
export interface TrialStatusResponse {
  shortId: string;
  state: 'pending' | 'provisioning' | 'ready' | 'failed';
  message: string;
}

/**
 * B1b — the operator console's payload (src/api/routes/tenancy-admin.ts).
 *
 * The signed licence DOCUMENT is deliberately absent: it is a bearer
 * credential for that tenant, and an admin list is the wrong place to hand
 * copies of it out. What is here is its summary.
 */
export interface AdminTenantLicense {
  serial: string;
  instanceId: string;
  apps: string[];
  features: string[];
  issuedAt: string;
  expiresAt: string;
  gracePeriodDays: number;
}

export interface AdminTenantRow {
  shortId: string;
  state: string;
  plan: string | null;
  schemaName: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  createdAt: string;
  expiresAt: string | null;
  legalHold: boolean;
  instanceKeyCount: number;
  lastEventAt: string | null;
  lastReconciledAt: string | null;
  lastReconcileError: string | null;
  license: AdminTenantLicense | null;
  /** A stored document that no longer parses — a control-plane problem, not "no licence". */
  licenseUnreadable: boolean;
}

export interface AdminTenantListResponse {
  /** False when STRIPE_SECRET_KEY is unset, in which case reconcile can only skip. */
  stripeConfigured: boolean;
  tenants: AdminTenantRow[];
}

export interface ReconcileResultRow {
  shortId: string;
  outcome: 'healed' | 'unchanged' | 'skipped' | 'failed';
  fromState?: string;
  toState?: string;
  reason?: string;
}

export interface ReconcileRunResponse {
  results: ReconcileResultRow[];
}

/**
 * The absolute URL a BI tool should be pointed at: the signed path from
 * `api.kpiExportLink` on this app's API origin. The bare export path is
 * useless to a connector on a deployment that enforces sign-in — it answers
 * "Missing or malformed Authorization header", which is how this was found.
 */
export function kpiExportUrl(signedPath: string): string {
  const base = baseUrl() || window.location.origin;
  return `${base}${signedPath}`;
}
